import settings from '@solinkcorp/settings';
import StorageAPI from '@solinkcorp/settings/StorageAPI';
import { isKiosk } from '@solinkcorp/utils';
import { type Auth0Error, type AuthorizeOptions, WebAuth } from 'auth0-js';
import { trackLogout } from 'core/analytics';
import Cookies from 'js-cookie';
import { type LogoutReason } from './authConstants';
import { authorize } from './callHome/callHomeAuth';
import { type CallHomeAuthorizeResult, type CallHomeUser } from './callHome/callHomeAuthTypes';
import { CACHED_AUTHORIZE_KEY, localhostAuthCache } from './callHome/localhostAuthCache';
import { type LogoutHandler, type UserSession } from './types';

const SNOWFLAKE_AUDIENCE = 'https://thoughtspot.solinkcloud.com/';

/**
 * Guards the top-level re-authorize redirect getAccessTokenForAudience fires
 * when checkSession comes back login_required. Cleared as soon as a
 * Snowflake-audience token is obtained (cached or freshly minted), so it
 * blocks at most one redirect per recovery attempt rather than for the whole
 * session.
 */
const SNOWFLAKE_AUDIENCE_RETRY_KEY = 'vd_snowflake_audience_retry';

/**
 * Shared by concurrent getAccessTokenForAudience callers (e.g. sibling NDE
 * dashboard widgets) so only the first login_required response triggers a
 * redirect. Siblings await this instead of rejecting under the first
 * caller's already-set retry guard.
 */
let redirectInFlight: Promise<never> | null = null;
let redirectInFlightReject: ((reason: Error) => void) | null = null;

const SNOWFLAKE_RETRY_TTL_MS = 60_000;

function clearSnowflakeRedirectGuards(): void {
  sessionStorage.removeItem(SNOWFLAKE_AUDIENCE_RETRY_KEY);
  redirectInFlight = null;
  redirectInFlightReject = null;
}

function isSnowflakeRetryGuardActive(): boolean {
  const raw = sessionStorage.getItem(SNOWFLAKE_AUDIENCE_RETRY_KEY);
  if (!raw) return false;
  const ts = Number(raw);
  if (Number.isNaN(ts) || Date.now() - ts > SNOWFLAKE_RETRY_TTL_MS) {
    sessionStorage.removeItem(SNOWFLAKE_AUDIENCE_RETRY_KEY);
    return false;
  }
  return true;
}

export type AuthCallbackResult = CallHomeAuthorizeResult & {
  returnTo?: string;
  snowflakeRecovery?: boolean;
};

/**
 * If a user is logged in, refresh their session and return the result.  If not,
 * returns undefined.
 *
 * Requests the Snowflake audience up front, same as login(), so the common
 * case never needs the separate lazy fetch in getAccessTokenForAudience — a
 * granted audience token is cached here already by the time NDE asks for one.
 */
export async function checkSession(
  bypassCache?: boolean,
): Promise<CallHomeAuthorizeResult | undefined> {
  const cached = localhostAuthCache(CACHED_AUTHORIZE_KEY, undefined, bypassCache);
  if (cached) return cached;

  const auth0 = getWebAuthInstance();
  const scope = 'openid profile email session:role-any';

  const { idToken, accessToken } = await new Promise<{ idToken: string; accessToken?: string }>(
    (resolve, reject) =>
      auth0.checkSession({ audience: SNOWFLAKE_AUDIENCE, scope }, (err, authResult) => {
        if (err) {
          reject(err);
        } else if (authResult && authResult.idToken) {
          resolve({
            idToken: authResult.idToken as string,
            accessToken: authResult.accessToken ?? undefined,
          });
        } else {
          reject(new Error('No idToken'));
        }
      }),
  );

  if (isTokenForAudience(accessToken, SNOWFLAKE_AUDIENCE)) {
    storeSnowflakeToken(accessToken);
    sessionStorage.removeItem(SNOWFLAKE_AUDIENCE_RETRY_KEY);
    redirectInFlight = null;
  }

  if (idToken) {
    return await authorize(idToken, accessToken, bypassCache);
  }
}

/** Redirect the user to the Auth0 login page. */
export function login(options?: {
  appState?: { returnTo: string; snowflakeRecovery?: boolean };
}): void {
  const auth0 = getWebAuthInstance();

  const authorizeOptions: AuthorizeOptions = {
    audience: SNOWFLAKE_AUDIENCE,
    scope: 'openid profile email session:role-any',
    ...(options?.appState ? { appState: options.appState } : {}),
  };
  if (isKiosk()) {
    authorizeOptions.prompt = 'login';
  }
  auth0.authorize(authorizeOptions);
}

/**
 * Called by the AuthCallback component when we receive an auth callback from Auth0.
 *
 * Always completes the login with whatever token pair this callback carries —
 * an IdP-initiated portal login (the Okta tile) hands back an opaque, no-audience
 * access token here rather than a Snowflake-scoped JWT, but that token is only
 * ever used to establish the Solink session below; it is never required to be
 * Snowflake-scoped. The Snowflake-audience access token is a separate, dual
 * credential fetched independently (and lazily) by getAccessTokenForAudience,
 * so a user who never opens NDE never pays for it and a slow/failed Snowflake
 * fetch never blocks or replaces the login itself.
 */
export async function handleAuthCallback(hash: string): Promise<AuthCallbackResult> {
  if (!/access_token|id_token|error/.test(hash)) {
    throw new Error('No id_token');
  }

  const auth0 = getWebAuthInstance();

  const { idToken, accessToken, appState } = await new Promise<{
    idToken: string;
    accessToken?: string;
    appState?: { returnTo?: string; snowflakeRecovery?: boolean };
  }>((resolve, reject) => {
    auth0.parseHash({ __enableIdPInitiatedLogin: true }, (err, authResult) => {
      const idToken = authResult?.idToken;
      if (idToken) {
        resolve({
          idToken,
          accessToken: authResult?.accessToken ?? undefined,
          appState: authResult?.appState,
        });
      } else {
        return reject(err);
      }
    });
  });

  if (isTokenForAudience(accessToken, SNOWFLAKE_AUDIENCE)) {
    storeSnowflakeToken(accessToken);
    clearSnowflakeRedirectGuards();
  }

  const result = await authorize(idToken, accessToken);
  return {
    ...result,
    ...(appState?.returnTo ? { returnTo: appState.returnTo } : {}),
    ...(appState?.snowflakeRecovery ? { snowflakeRecovery: true } : {}),
  };
}

/**
 * Logout the current user, and clear their current sessions.
 *
 * @param user - The logged in user, if any.  We only need the user or the impersonated
 *   user, we don't care about the impersonator.
 * @param logoutHandlers - Array of all handlers to run when we logout the user.
 * @param reason - The reason the user is logging out.
 * @param [returnTo] = The page to go to after logging out.
 * @param analyticsData - Data for analytics logging
 */
export function logout(
  user: UserSession | undefined,
  logoutHandlers: { current: LogoutHandler }[],
  reason: LogoutReason,
  returnTo?: string,
  analyticsData?: { source: string; message: string },
): void {
  // Un-set any cookies we set in authenticationActions.
  user?.cookies?.forEach((cookie) => {
    console.log(`-logout- Removing cookie ${cookie.name}`);
    Cookies.remove(cookie.name, { domain: settings.domain_solink });
  });

  // Note that this will clear the currently impersonated user and the guest user,
  // if there are any.
  StorageAPI.clearNonPersistent();
  Cookies.remove(SNOWFLAKE_TOKEN_KEY, { domain: settings.domain_solink, path: '/' });
  clearSnowflakeRedirectGuards();

  trackLogout(reason, analyticsData);

  Promise.resolve()
    .then(async () => {
      // Run each logout handler. If any handler fails, log and continue
      // (because we don't want logout to not log you out...).
      for (const handler of logoutHandlers) {
        try {
          await handler.current(reason);
        } catch (err) {
          console.error(`Error running logout handler`, err);
        }
      }

      // Logout of auth0.  Note if we're logged in as a guest, this does nothing.
      const auth0 = getWebAuthInstance();
      auth0.logout({
        clientID: settings.auth0_clientID,
        returnTo: returnTo ?? `${window.location.origin}/login`,
      });
    })
    .catch((err) => console.error('Error logging out!', err));
}

/**
 * Returns true if the given user is an impersonator.
 */
export function isImpersonator(user: CallHomeUser): boolean {
  return ['solink', 'cloud', 'reseller'].includes(user.userType);
}

/**
 * Get an access token for a specific OAuth audience.
 *
 * Prefers the token captured during the login callback (stored as a cookie),
 * which avoids the hidden-iframe checkSession flow that fails when third-party
 * cookies are blocked (e.g. Chrome incognito). Falls back to checkSession for
 * environments where third-party cookies are available, and if that comes
 * back `login_required`, does one top-level re-authorize instead: the hidden
 * iframe is a third-party context and can't see the Auth0 session cookie when
 * third-party cookies are blocked, but a full-page navigation to Auth0 is
 * first-party there and carries the cookie regardless.
 */
export async function getAccessTokenForAudience(audience: string): Promise<string> {
  const stored = readStoredSnowflakeToken(audience);
  if (stored) {
    clearSnowflakeRedirectGuards();
    return stored;
  }

  const auth0 = getWebAuthInstance();
  const scope = 'openid profile email session:role-any';

  return new Promise<string>((resolve, reject) => {
    auth0.checkSession({ audience, scope }, (err, authResult) => {
      if (err) {
        if (err.error === 'login_required') {
          if (redirectInFlight) {
            redirectInFlight.catch(reject);
            return;
          }
          if (!isSnowflakeRetryGuardActive()) {
            sessionStorage.setItem(SNOWFLAKE_AUDIENCE_RETRY_KEY, String(Date.now()));
            const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
            redirectInFlight = new Promise<never>((_, rej) => {
              redirectInFlightReject = rej;
            });
            try {
              login({ appState: { returnTo, snowflakeRecovery: true } });
              return;
            } catch (loginError) {
              const error = loginError instanceof Error ? loginError : new Error('Redirect failed');
              if (redirectInFlightReject) redirectInFlightReject(error);
              clearSnowflakeRedirectGuards();
              reject(error);
              return;
            }
          }
        }
        reject(normalizeAuth0Error(err));
      } else if (authResult?.accessToken) {
        clearSnowflakeRedirectGuards();
        storeSnowflakeToken(authResult.accessToken);
        resolve(authResult.accessToken);
      } else {
        reject(new Error('No accessToken in auth result'));
      }
    });
  });
}

/**
 * auth0-js rejects with a plain `{ error, errorDescription }` object rather
 * than an `Error`, so callers (and Sentry) see "[object Object]" instead of
 * the Auth0 error code. Wrap it so the code stays visible and groupable.
 */
function normalizeAuth0Error(err: Auth0Error): Error {
  const description = err.errorDescription ?? err.error_description ?? '';
  return new Error(description ? `${err.error}: ${description}` : err.error, { cause: err });
}

/** True if `token` is a JWT whose `aud` includes `audience` and hasn't expired. */
export function isTokenForAudience(token: string | undefined, audience: string): token is string {
  if (!token) return false;
  try {
    const payload = decodeJwtPayload(token);
    const aud = payload.aud;
    const hasAudience = Array.isArray(aud) ? aud.includes(audience) : aud === audience;
    const live = typeof payload.exp !== 'number' || Date.now() < payload.exp * 1000;
    return hasAudience && live;
  } catch (err) {
    // Opaque token (e.g. the encrypted access token an IdP-initiated login
    // gets when no audience was requested), or not a JWT at all.
    console.debug('isTokenForAudience: token is not a decodable JWT', err);
    return false;
  }
}

function decodeJwtPayload(token: string): { aud?: string | string[]; exp?: number } {
  return JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
}

const SNOWFLAKE_TOKEN_KEY = 'vd_snowflake_access_token';
/**
 * Treat the cached token as spent this long before its stated `exp`.
 * Snowflake validates against its own clock: with no margin, a browser whose
 * clock runs slow keeps sending a token the server already considers dead,
 * and every Explorer query fails until the local clock catches up. Matches
 * the mobile package's own refresh buffer.
 */
const SNOWFLAKE_TOKEN_EXPIRY_SKEW_MS = 5 * 60 * 1000;

function storeSnowflakeToken(token: string): void {
  try {
    const payload = decodeJwtPayload(token);
    const expires = typeof payload.exp === 'number' ? new Date(payload.exp * 1000) : undefined;
    Cookies.set(SNOWFLAKE_TOKEN_KEY, token, {
      domain: settings.domain_solink,
      secure: true,
      expires,
      path: '/',
    });
  } catch {
    // ignore — cookie write failure is non-fatal; callhome Set-Cookie is the backup
  }
}

function readStoredSnowflakeToken(audience: string): string | null {
  const token = Cookies.get(SNOWFLAKE_TOKEN_KEY);
  if (!isTokenForAudience(token, audience)) return null;

  // isTokenForAudience already decoded token successfully, so this can't throw.
  const payload = decodeJwtPayload(token);
  const insideSkewBuffer =
    typeof payload.exp === 'number' &&
    Date.now() >= payload.exp * 1000 - SNOWFLAKE_TOKEN_EXPIRY_SKEW_MS;
  return insideSkewBuffer ? null : token;
}

/**
 * Drop the cached Snowflake token so the next getAccessTokenForAudience call
 * mints a fresh one. Callers use this when the server rejects the token as
 * invalid — the cached copy still looks live locally, so without clearing it
 * a retry would just replay the same dead token.
 */
export function clearStoredSnowflakeToken(): void {
  Cookies.remove(SNOWFLAKE_TOKEN_KEY, { domain: settings.domain_solink, path: '/' });
}

function getWebAuthInstance() {
  return new WebAuth({
    domain: settings.auth0_domain,
    clientID: settings.auth0_clientID,
    redirectUri: `${origin}/login/callback`,
    responseType: `token id_token`,
    scope: `openid profile email`,
  });
}
